import {Data, Int} from '@store/@types/util';
import {Moment} from 'moment';
import createMatchFinder from 'utils/createMatchFinder';

const clearableProps = ['searchTerms', 'startDate', 'endDate'];

interface InboxFiltersInterface {
  unviewedOnly: boolean;
  pinnedOnly: boolean;
  searchTerms: string;
  startDate: Moment;
  endDate: Moment;
  folderId: Int;
  tagId: Int;
  viewAll: boolean;
}

export default class InboxFilters extends Data<InboxFiltersInterface>({
  unviewedOnly: false,
  pinnedOnly: false,
  searchTerms: undefined,
  startDate: undefined,
  endDate: undefined,
  folderId: undefined,
  tagId: undefined,
  viewAll: undefined,
}) {
  /**
     The version of the filters that will be sent in the inbox item search request
  */
  searchValue() {
    if (!this.searchTerms) {
      return this.delete('searchTerms');
    }
    const trimmed = this.searchTerms.trim();
    return trimmed.length > 1 ? this.set('searchTerms', trimmed) : this.delete('searchTerms');
  }

  /**
     The version of the filters that will be sent in the tag request
     (filters used by tag endpoint to count item totals)
  */
  tagSearchValue() {
    return this.searchValue().delete('tagId');
  }

  /**
     Some filters can be cleared by the user
  */
  isClearable() {
    return clearableProps.some(prop => !!this[prop]);
  }

  /**
   * Check if the filter is set (i.e. any explicit filtering is set, so folderId and tagId don't ccount)
   * @returns {boolean}
   */
  isSet() {
    return this.isClearable() || this.pinnedOnly || this.unviewedOnly;
  }

  clear() {
    return clearableProps.reduce((self, prop) => self.delete(prop), this);
  }

  /**
     Function to determine if a given mail item matches the filters
     Only needed to update the UI after updating the mail items so
     a full data reload won't be needed
  */
  isMatch(item) {
    if (
      (this.pinnedOnly && !item.pinned) ||
      (this.unviewedOnly && item.viewedDate) ||
      (this.startDate && this.startDate.isAfter(item.created)) ||
      (this.endDate && this.endDate.isBefore(item.created)) ||
      (this.folderId && this.folderId !== item.folderId) ||
      (this.tagId && this.tagId !== item.tagId)
    ) {
      return false;
    }
    if (this.searchTerms) {
      const findMatches = createMatchFinder(this.searchTerms);
      const isSearchTextMatching = [
        item.call.description,
        item.call.caller.getFullName(),
        JSON.stringify(item.context),
      ].some(text => findMatches(text).size > 0);
      if (!isSearchTextMatching) {
        return false;
      }
    }
    return true;
  }
}
